import { useQuery } from '@apollo/client';

import Button from '@/componentsv2/Button';

import GovernanceInbox from './GovernanceInbox';
import { MY_GOVERNANCE_QUEUE } from './operations';
import { fromGovernanceQueue } from './viewModel';

/**
 * `/approvals` — the live governance approver queue.
 *
 * The flag gate is one level up in `ApprovalsRoute`; this only renders when the
 * feature is on.
 *
 * Until `myGovernanceQueue` landed (gap G3, ENG-5604) the route passed a
 * hardcoded empty array, so a real approver with a real pending decision was
 * told "All caught up". The queue is viewer-scoped server-side, so there is no
 * id to pass and no client-side filtering to do.
 *
 * An empty queue is a result, not a denial — an approver with nothing to decide
 * and a viewer who approves nothing both land on the same empty state, which is
 * why there is no permission branch here.
 */
export default function ApprovalsPage(): JSX.Element {
  const { data, loading, error, refetch } = useQuery(MY_GOVERNANCE_QUEUE, {
    // The queue changes underneath the approver as counterparties act, and a
    // decision made here removes its own row, so a cached queue is only good
    // for the first paint — always revalidate against the network.
    fetchPolicy: 'cache-and-network',
  });

  // No skeleton: the inbox's own header and empty state are the loading shape,
  // and swapping them for a placeholder shifts layout on every refetch.
  if (loading && !data) return <GovernanceInbox queue={[]} />;

  if (error) {
    return (
      <div className="mx-auto flex w-full max-w-[920px] flex-col px-16 py-24 sm:px-24">
        <div className="rounded-lg border border-surface-border bg-surface-card p-24">
          <h1 className="text-on-surface hd-5">We couldn’t load your approvals.</h1>
          <p className="mt-8 text-on-surface-muted body-2">
            The connection may have dropped. Try again in a moment.
          </p>
          <div className="mt-16">
            <Button onClick={() => void refetch()}>Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  return <GovernanceInbox queue={fromGovernanceQueue(data?.myGovernanceQueue ?? [])} />;
}
